// One profile's search, drawn.
//
// The score is the hero number and the line is context — which is the right way
// round for a search somebody checks on twice a day, where "has it moved" is
// the whole question and the shape only matters once the answer is yes.

import { type TunerProfile } from "src/engine/net/tuner";
import { Pill, styled, Text } from "../kit";
import {
  islandIsLive,
  type Pending,
  pendingOf,
  pathOn,
  progressOf,
  type Scale,
  scaleOf,
  type SeriesShown,
  since
} from "../tunerView";
import { Decomposition } from "./Decomposition";
import { SERIES_COLOUR, SERIES_DASH } from "./palette";
import { Card, Legend, Row } from "./parts";

const SPARK_WIDTH = 220;
const SPARK_HEIGHT = 44;

const Chart = styled("svg")({ display: "block", overflow: "visible" });

const Caption = styled(Text)({ display: "block", marginTop: 4 });

/**
 * The live generation, as a mark rather than a point on the line.
 *
 * **Past the right edge on purpose.** Every other mark here is a recorded
 * ledger entry and the x axis is their index; this one has no index, because
 * nothing has recorded it. Drawing it *on* the axis would claim a datapoint
 * that does not exist yet, and connecting it to the last one would draw a
 * segment of a line that may never be drawn.
 */
function PendingMark({
  pending,
  scale,
  width,
  height
}: {
  pending: Pending;
  scale: Scale;
  width: number;
  height: number;
}): React.ReactElement {
  const range = scale.high - scale.low;
  const y = range === 0 ? height / 2 : height - ((pending.score - scale.low) / range) * height;
  const colour = pending.ahead ? SERIES_COLOUR.pending : SERIES_COLOUR.offers;
  return (
    <g>
      {/* A rule at its height, so it can be read against the lines it is not
          on. Faint, because it is a reading rather than a series. */}
      <line
        x1={0}
        x2={width + 6}
        y1={y}
        y2={y}
        stroke={colour}
        strokeOpacity={pending.ahead ? 0.5 : 0.28}
        strokeWidth={1}
        strokeDasharray={SERIES_DASH.pending}
      />
      <circle cx={width + 6} cy={y} r={pending.ahead ? 3.5 : 2.5} fill={colour} />
    </g>
  );
}

export function ProfileRow({
  profile,
  now,
  shown
}: {
  profile: TunerProfile;
  now: number;
  shown: SeriesShown;
}): React.ReactElement {
  const progress = progressOf(profile);
  const scores = progress.current?.points.map((point) => point.score) ?? [];
  const pending = pendingOf(profile, progress.best, now);
  const drawFoe = shown.foe && progress.foeSeen > 1;
  const drawPending = shown.pending && pending !== null;
  // **Scaled on what is drawn, not on what exists.** One axis across the
  // series is still right — the loss line says nothing except by comparison
  // with the score above it — but a series that is switched *off* is not being
  // compared with anything, and leaving it in the scale flattens the lines that
  // are into a band across the middle. That is the whole point of the toggles.
  const scale = scaleOf(
    shown.offers ? scores : [],
    shown.best ? progress.best : [],
    drawFoe ? progress.foe : [],
    drawPending ? [pending.score] : []
  );
  const offers = pathOn(scores, scale, SPARK_WIDTH, SPARK_HEIGHT);
  const best = pathOn(progress.best, scale, SPARK_WIDTH, SPARK_HEIGHT);
  const foe = pathOn(progress.foe, scale, SPARK_WIDTH, SPARK_HEIGHT);
  const live = profile.islands.filter((island) => islandIsLive(island, now));

  return (
    <Card solid>
      <Row>
        <Text weight={700} size={16}>
          {profile.name}
        </Text>
        <Text role="display" tone="brand" weight={700} size={22}>
          {Math.round(profile.score).toLocaleString()}
        </Text>
        <Text tone="dim" size={12}>
          gen {profile.generation}
          {profile.at > 0 ? ` · ${since(profile.at, now)}` : ""}
        </Text>
        {live.length > 0 && (
          <Pill>
            <Text role="display" tone="cream" size={11} weight={700}>
              {live.length} searching
            </Text>
          </Pill>
        )}
      </Row>

      {scores.length > 1 ? (
        <div>
          <Chart
            width={SPARK_WIDTH}
            height={SPARK_HEIGHT}
            viewBox={`0 0 ${SPARK_WIDTH} ${SPARK_HEIGHT}`}
            role="img"
            aria-label={`${profile.name}: ${scores.length} champions offered, best ${Math.round(
              progress.best[progress.best.length - 1] ?? 0
            )}${pending ? `, island ${pending.island} now at ${Math.round(pending.score)}` : ""}`}
          >
            {/* What was offered, and the best it had reached — the second is
                the one that answers "is this still finding anything", because
                offers bounce and a flat best line is a search that has
                stalled. */}
            {shown.offers && (
              <path
                d={offers}
                fill="none"
                stroke={SERIES_COLOUR.offers}
                strokeOpacity={0.45}
                strokeWidth={2}
              />
            )}
            {/* The opposition, on the same games and the same axis. Dashed and
                in the refusal colour: it is a control rather than a second
                search, and the thing to read off it is the *gap* — both lines
                rising together is a batch that got easier, not a candidate that
                got better. Broken where nothing recorded one. */}
            {drawFoe && (
              <path
                d={foe}
                fill="none"
                stroke={SERIES_COLOUR.foe}
                strokeOpacity={0.8}
                strokeWidth={1.5}
                strokeDasharray={SERIES_DASH.foe}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}
            {shown.best && (
              <path
                d={best}
                fill="none"
                stroke={SERIES_COLOUR.best}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}
            {/* **Where the search is now**, which the line cannot say: the
                ledger holds records only, so a flat line covers both "still
                scoring just under the record" and "fallen off a cliff". Drawn
                past the right edge and off the line, because it is not a
                recorded point — a connector to it would claim it had been. */}
            {drawPending && (
              <PendingMark
                pending={pending}
                scale={scale}
                width={SPARK_WIDTH}
                height={SPARK_HEIGHT}
              />
            )}
          </Chart>
          <Caption tone="dim" size={11}>
            {scores.length} offered on this basis
            {drawPending && (
              <>
                {" · "}
                <Text tone={pending.ahead ? "cream" : "dim"} weight={700} size={11}>
                  now {Math.round(pending.score).toLocaleString()}
                  {pending.ahead ? " ▲" : ""}
                </Text>
              </>
            )}
            {drawFoe && (
              <>
                {" · "}
                <Legend
                  text="loss line"
                  title="The loss line"
                  rule="The dashed line is what the armies sitting opposite scored on the same games, under this same goal. It is a control and never a target: a score on its own cannot say whether the search found something or the batch got easier, and the gap between the two lines can. Nothing selects on it."
                />
              </>
            )}
            {progress.earlier > 0 && (
              <>
                {" · "}
                <Legend
                  text={`${progress.earlier} earlier ${progress.earlier === 1 ? "basis" : "bases"} hidden`}
                  title="Earlier bases"
                  rule="A score is a number from whatever measurement basis it was taken on — the goal, the batch and the versions. Those runs are not comparable with this one, so drawing them on one axis would show a step change where the ruler moved and read as progress."
                />
              </>
            )}
          </Caption>
        </div>
      ) : (
        <Caption tone="dim" size={12}>
          no history on the current basis yet
        </Caption>
      )}

      {profile.goal && (
        <Decomposition metrics={profile.metrics} goal={profile.goal} lean={profile.lean} />
      )}
    </Card>
  );
}
